import { appInsights, SeverityLevel, isAppInsightsInitialized } from '../config/appInsights';

async function hashSensitiveData(data: string): Promise<string> {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
}

class TelemetryService {
  private isInitialized: boolean;
  private userIdHash: string | null = null;

  constructor() {
    this.isInitialized = isAppInsightsInitialized();
  }

  async hashSensitiveData(data: string): Promise<string> {
    return hashSensitiveData(data);
  }

  private sanitizeProperties(properties?: Record<string, any>): Record<string, any> | undefined {
    if (!properties) return properties;

    const sanitized = { ...properties };
    const sensitiveKeys = ['password', 'token', 'secret', 'apiKey', 'ssn', 'creditCard'];

    Object.keys(sanitized).forEach((key) => {
      const lowerKey = key.toLowerCase();
      if (sensitiveKeys.some((sensitive) => lowerKey.includes(sensitive))) {
        delete sanitized[key];
      }

      if (lowerKey.includes('email') && typeof sanitized[key] === 'string') {
        delete sanitized[key]; // Remove to avoid logging PII
      }

      if (lowerKey.includes('phone') && typeof sanitized[key] === 'string') {
        delete sanitized[key]; // Remove to avoid logging PII
      }
    });

    return sanitized;
  }

  trackEvent(name: string, properties?: Record<string, any>, measurements?: Record<string, number>) {
    if (this.isInitialized && appInsights) {
      appInsights.trackEvent({
        name,
        properties: this.sanitizeProperties(properties),
        measurements
      });
    }
  }

  trackException(error: Error, severityLevel: SeverityLevel = SeverityLevel.Error, properties?: Record<string, any>) {
    if (this.isInitialized && appInsights) {
      appInsights.trackException({
        exception: error,
        severityLevel,
        properties: this.sanitizeProperties(properties)
      });
    } else {
      // Fallback to console if AI not initialized
      console.error('Exception:', error, properties);
    }
  }

  trackTrace(message: string, severityLevel: SeverityLevel = SeverityLevel.Information, properties?: Record<string, any>) {
    if (this.isInitialized && appInsights) {
      appInsights.trackTrace({
        message,
        severityLevel,
        properties: this.sanitizeProperties(properties)
      });
    }
  }

  trackPageView(name?: string, url?: string, properties?: Record<string, any>, measurements?: Record<string, number>) {
    if (this.isInitialized && appInsights) {
      appInsights.trackPageView({
        name,
        uri: url || window.location.pathname,
        properties: this.sanitizeProperties(properties),
        measurements
      });
    }
  }

  trackMetric(name: string, average: number, properties?: Record<string, any>) {
    if (this.isInitialized && appInsights) {
      appInsights.trackMetric({
        name,
        average,
        properties: this.sanitizeProperties(properties)
      });
    }
  }

  trackDependency(
    id: string,
    method: string,
    absoluteUrl: string,
    pathName: string,
    totalTime: number,
    success: boolean,
    resultCode: number,
    properties?: Record<string, any>
  ) {
    if (this.isInitialized && appInsights) {
      appInsights.trackDependencyData({
        id,
        name: `${method} ${pathName}`,
        duration: totalTime,
        success,
        responseCode: resultCode,
        type: 'Ajax',
        data: absoluteUrl,
        target: absoluteUrl,
        properties: this.sanitizeProperties(properties)
      });
    }
  }

  async setUserId(userId: string, accountId?: string) {
    if (this.isInitialized && appInsights) {
      this.userIdHash = await hashSensitiveData(userId);

      appInsights.setAuthenticatedUserContext(this.userIdHash, accountId ? await hashSensitiveData(accountId) : undefined, true);

      // Track user session start
      this.trackEvent('UserSessionStarted', {
        userIdHash: this.userIdHash,
        timestamp: new Date().toISOString()
      });
    }
  }

  clearUserId() {
    if (this.isInitialized && appInsights) {
      if (this.userIdHash) {
        this.trackEvent('UserSessionEnded', {
          userIdHash: this.userIdHash,
          timestamp: new Date().toISOString()
        });
      }

      appInsights.clearAuthenticatedUserContext();
      this.userIdHash = null;
    }
  }

  getUserIdHash(): string | null {
    return this.userIdHash;
  }

  startTrackEvent(name: string) {
    if (this.isInitialized && appInsights) {
      appInsights.startTrackEvent(name);
    }
  }

  stopTrackEvent(name: string, properties?: Record<string, any>, measurements?: Record<string, number>) {
    if (this.isInitialized && appInsights) {
      appInsights.stopTrackEvent(name, this.sanitizeProperties(properties), measurements);
    }
  }

  trackComponentPerformance(componentName: string, renderTime: number, properties?: Record<string, any>) {
    this.trackMetric(`Component.${componentName}.RenderTime`, renderTime, {
      ...properties,
      component: componentName
    });
  }

  trackApiPerformance(endpoint: string, method: string, duration: number, statusCode: number, properties?: Record<string, any>) {
    this.trackMetric('API.ResponseTime', duration, {
      endpoint,
      method,
      statusCode: statusCode.toString(),
      ...this.sanitizeProperties(properties)
    });
  }

  flush() {
    if (this.isInitialized && appInsights) {
      appInsights.flush();
    }
  }

  trackNavigation(from: string, to: string, properties?: Record<string, any>) {
    this.trackEvent('Navigation', {
      from,
      to,
      ...this.sanitizeProperties(properties)
    });
  }

  trackFormInteraction(
    formName: string,
    action: 'started' | 'submitted' | 'abandoned' | 'validation_failed',
    properties?: Record<string, any>
  ) {
    this.trackEvent(`Form.${action}`, {
      formName,
      action,
      ...this.sanitizeProperties(properties)
    });
  }

  trackUserAction(actionName: string, elementType?: string, elementId?: string, properties?: Record<string, any>) {
    this.trackEvent('UserAction', {
      action: actionName,
      elementType,
      elementId,
      ...this.sanitizeProperties(properties)
    });
  }

  trackFeatureUsage(featureName: string, properties?: Record<string, any>) {
    this.trackEvent('FeatureUsed', {
      feature: featureName,
      ...this.sanitizeProperties(properties)
    });
  }

  trackError(errorMessage: string, errorCode?: string, properties?: Record<string, any>) {
    this.trackTrace(`Error: ${errorMessage}`, SeverityLevel.Error, {
      errorCode,
      ...this.sanitizeProperties(properties)
    });
  }
}

export const telemetryService = new TelemetryService();
